import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, NgZone, OnDestroy, output, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { marked } from 'marked';
import { reportSections } from './native-response/report-sections';
import { ResponseDomRenderer } from './response-dom-renderer';

/** Owned by the message, so changing its presentation never restarts displayed text. */
export interface ResponseRevealState { shown: number; content: string }

/** Reveal text within its final markup, so bold text, links and lists never flash raw Markdown. */
export function revealResponseHtml(html: string, characters: number, fadeCharacters=0): string {
  const template = document.createElement('template');
  template.innerHTML = html;
  let remaining = Math.ceil(characters),position=0;
  const trim = (parent: Node) => {
    for (const child of Array.from(parent.childNodes)) {
      if (remaining <= 0) { parent.removeChild(child); continue; }
      if (child.nodeType === Node.TEXT_NODE) {
        const text = Array.from(child.textContent ?? '');
        const visible=text.slice(0,remaining),fadeStart=Math.max(0,Math.floor(characters-fadeCharacters)-position);
        if(fadeCharacters>0&&fadeStart<visible.length){
          const fragment=document.createDocumentFragment();
          fragment.append(document.createTextNode(visible.slice(0,fadeStart).join('')));
          visible.slice(fadeStart).forEach((character,index)=>{
            const span=document.createElement('span');span.textContent=character;
            const progress=Math.min(1,Math.max(0,(characters-position-fadeStart-index)/fadeCharacters));
            span.className=`reveal-fragment reveal-soft-${Math.round(progress*8)}`;fragment.append(span);
          });parent.replaceChild(fragment,child);
        }else child.textContent=visible.join('');
        position+=visible.length;
        remaining -= Math.min(text.length, remaining);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        trim(child);
        if (!child.childNodes.length && !['BR', 'HR', 'IMG'].includes(child.nodeName)) parent.removeChild(child);
      }
    }
  };
  trim(template.content);
  return template.innerHTML;
}

@Component({
  selector: 'app-response-text',
  template: `<div #prose class="prose"></div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`:host{display:block;min-width:0}.prose{max-width:none;line-height:1.7;overflow-wrap:anywhere}
    :host ::ng-deep .reveal-soft-0{opacity:.6}:host ::ng-deep .reveal-soft-1{opacity:.65}:host ::ng-deep .reveal-soft-2{opacity:.7}:host ::ng-deep .reveal-soft-3{opacity:.75}:host ::ng-deep .reveal-soft-4{opacity:.8}:host ::ng-deep .reveal-soft-5{opacity:.85}:host ::ng-deep .reveal-soft-6{opacity:.9}:host ::ng-deep .reveal-soft-7{opacity:.95}:host ::ng-deep .reveal-soft-8{opacity:1}
    :host ::ng-deep .suggestion{margin:20px 0 0;padding:16px 20px;background:#edf4f9;border-left:3px solid #83a9d4;border-radius:0 8px 8px 0}
    :host ::ng-deep .suggestion h3{display:flex;align-items:center;gap:8px;margin:0 0 8px;font-size:.95rem;font-weight:600}
    :host ::ng-deep .suggestion h3::before{content:'lightbulb';font-family:'Material Icons Outlined';font-size:20px;color:#5279a5;font-weight:normal}
    :host ::ng-deep .suggestion+p{margin-top:16px}`],
})
export class ResponseTextComponent implements AfterViewInit, OnDestroy {
  content = input('');
  live = input(false);
  streaming = input(false);
  revealState = input<ResponseRevealState>();
  highlightSuggestion = input(true);
  revealingChange = output<boolean>();
  private sanitizer = inject(DomSanitizer);
  private readonly zone = inject(NgZone);
  @ViewChild('prose', {static: true}) private prose?: ElementRef<HTMLElement>;
  private readonly renderer = new ResponseDomRenderer();
  private renderedDocument?: DocumentFragment;
  private directDocument?: DocumentFragment;
  private shown = 0;
  private frame?: number;
  private previousContent = '';
  private revealing?: boolean;
  private targetLength = 0;
  private lastFrame?: number;
  private wasStreaming = false;
  private settlingMs = 0;
  private reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  private motionChanged = () => { if (this.reducedMotion.matches) this.finish(); };

  private document = computed(() => {
    const content = this.content();
    const sections = this.highlightSuggestion() ? reportSections(content) : { body: content, suggestion: null, after: '' };
    const parse = (text: string) => marked.parse(text, { async: false }) as string;
    const html = parse(sections.body) + (sections.suggestion !== null
      ? `<aside class="suggestion" aria-label="Suggested next step"><h3>Suggested next step</h3>${parse(sections.suggestion)}</aside>` : '') + parse(sections.after);
    const template = document.createElement('template');
    template.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    for (const link of Array.from(template.content.querySelectorAll('a'))) { link.target = '_blank'; link.rel = 'noopener noreferrer'; }
    return { fragment: template.content, length: Array.from(template.content.textContent ?? '').length };
  });
  private renderVisible(): void {
    const host = this.prose?.nativeElement;
    if (!host) return;
    const parsed = this.document();
    // Saved answers need one normal DOM insertion, with no reveal bookkeeping.
    if (!this.renderedDocument && !this.streaming() && this.shown >= this.targetLength) {
      if (this.directDocument !== parsed.fragment) host.replaceChildren(parsed.fragment.cloneNode(true));
      this.directDocument = parsed.fragment;
      return;
    }
    if (this.renderedDocument !== parsed.fragment) {
      this.renderer.setDocument(parsed.fragment);
      this.renderedDocument = parsed.fragment;
    }
    this.renderer.render(host, this.shown, this.shown >= this.targetLength ? 0 : 16);
  }
  ngAfterViewInit(): void { this.renderVisible(); }
  private queueFrame(): void {
    this.zone.runOutsideAngular(() => {this.frame = requestAnimationFrame(this.tick);});
  }

  constructor() {
    this.reducedMotion.addEventListener('change', this.motionChanged);
    effect(() => {
      const content = this.content();
      this.targetLength = this.document().length;
      const saved=this.revealState();
      if(!this.previousContent&&saved){this.shown = Math.min(saved.shown,this.targetLength);this.previousContent=saved.content;}
      // History corrections should update in place, not replay an already displayed answer.
      const correction = !!this.previousContent && !content.startsWith(this.previousContent);
      this.previousContent = content;
      if(saved)saved.content=content;
      const streaming=this.streaming(),ended=this.wasStreaming&&!streaming;this.wasStreaming=streaming;
      if(ended)this.settlingMs=180;
      if (!this.live() || this.reducedMotion.matches || correction || (!streaming&&!this.settlingMs)) { this.finish(); return; }
      if (!this.targetLength) {this.finish();return;}
      // Make the first available words readable immediately; smoothing must not
      // hold the first answer text behind a decorative typing pass.
      if (!this.shown) this.shown = Math.min(16, this.targetLength);
      this.renderVisible();
      this.remember();
      if(this.shown>=this.targetLength){this.setRevealing(false);return;}
      this.setRevealing(true);
      if (this.frame === undefined) this.queueFrame();
    });
  }

  private tick = (timestamp: number) => {
    const elapsed = Math.max(1, this.lastFrame === undefined ? 16 : timestamp - this.lastFrame);
    this.lastFrame = timestamp;
    // Smooth the arriving buffer, not the entire answer length. A small rolling tail
    // bridges network packets without delaying completion with a second typing pass.
    const backlog=this.targetLength-this.shown;
    const advance=this.settlingMs ? backlog*Math.min(1,elapsed/this.settlingMs) : Math.max(40,backlog/.26)*elapsed/1000;
    if(this.settlingMs)this.settlingMs=Math.max(0,this.settlingMs-elapsed);
    // A slow or resumed frame catches up to the received text immediately.
    // Model output is unchanged; only the optional reveal tail is skipped.
    this.shown = elapsed >= 80 ? this.targetLength : Math.min(this.targetLength, this.shown + advance);
    this.renderVisible();
    this.remember();
    this.frame = undefined;
    if (this.shown < this.targetLength) this.queueFrame();
    else { this.lastFrame = undefined; this.setRevealing(false); }
  };
  private setRevealing(value: boolean) {
    if (this.revealing !== value) { this.revealing = value; this.zone.run(() => this.revealingChange.emit(value)); }
  }
  private finish() {
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    this.frame = undefined; this.lastFrame = undefined;this.settlingMs=0;
    this.shown = this.targetLength;
    this.renderVisible();
    this.remember();
    this.setRevealing(false);
  }
  private remember(){const state=this.revealState();if(state)state.shown=this.shown;}
  ngOnDestroy() {
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    this.reducedMotion.removeEventListener('change', this.motionChanged);
  }
}
